<template>
  <div class="eu-call-scheme-mapping-container">
    <h1 class="eu-call-scheme-mapping-title">EU call scheme mapping</h1>
    <p class="eu-call-scheme-mapping-intro">
      Map a call identifier prefix (for example
      <code>HORIZON-CL5-</code>) to the parent scheme that newly imported EU calls
      with that prefix should be linked to. Each prefix can be used only once.
    </p>

    <div class="dx-card-custom">
      <DxDataGrid
        :data-source="dataSource"
        key-expr="id"
        :show-borders="true"
        :show-row-lines="true"
        :column-auto-width="true"
        :no-data-text="noDataText"
        aria-label="EU call scheme mapping"
      >
        <DxSearchPanel
          :visible="true"
          :width="300"
          placeholder="Search prefix or scheme..."
        />
        <DxPaging :page-size="25" />
        <DxPager
          :visible="true"
          :show-navigation-buttons="true"
          :show-info="true"
          :show-page-size-selector="true"
          :allowed-page-sizes="[25, 50, 100]"
        />
        <DxEditing
          :allow-adding="true"
          :allow-updating="true"
          :allow-deleting="true"
          :use-icons="true"
          mode="row"
        />
        <DxColumn
          data-field="callIdentifierPrefix"
          caption="Call identifier prefix"
          sort-order="asc"
        >
          <DxRequiredRule message="Call identifier prefix is required" />
        </DxColumn>
        <DxColumn
          data-field="schemeId"
          caption="Scheme"
          :editor-options="{ searchEnabled: true }"
        >
          <DxRequiredRule message="Scheme is required" />
          <DxLookup
            :data-source="schemes"
            value-expr="id"
            display-expr="label"
          />
        </DxColumn>
      </DxDataGrid>
    </div>
  </div>
</template>

<script setup>
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxLookup,
  DxRequiredRule,
  DxPaging,
  DxPager,
  DxSearchPanel
} from "devextreme-vue/data-grid";
import { ref, onMounted } from "vue";
import euCallSchemeMappingService from "../services/euCallSchemeMapping.service.js";
import { createEuCallSchemeMappingStore } from "../store/custom.store.js";

const schemes = ref([]);
const noDataText = "No mappings yet. Use the + button to add one.";

const dataSource = createEuCallSchemeMappingStore();

onMounted(async () => {
  schemes.value = await euCallSchemeMappingService.getEnglishSchemesForSelect(
    ""
  );
});
</script>

<style scoped>
.eu-call-scheme-mapping-container {
  padding: 24px 36px;
}

.eu-call-scheme-mapping-title {
  font-size: 1.6rem;
  margin-bottom: 8px;
}

.eu-call-scheme-mapping-intro {
  margin-bottom: 16px;
  max-width: 900px;
}

/* Search panel: move the magnifier icon to the right edge so it never
   overlaps the placeholder/search text */
:deep(.dx-datagrid-search-panel .dx-icon-search) {
  position: absolute;
  top: 50%;
  inset-inline-start: auto;
  inset-inline-end: 9px;
  margin-top: 0;
  transform: translateY(-50%);
}

:deep(.dx-datagrid-search-panel .dx-texteditor-input),
:deep(.dx-datagrid-search-panel .dx-placeholder)::before {
  padding-inline-start: 9px;
  padding-inline-end: 28px;
}
</style>
